// Transport for the corporate-site → fbinsights-api calls.
const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

export interface StartFreeApplication {
  email: string;
  legalName: string;
  brandName: string;
  contactName: string;
  locationState: string;
  website: string;
  opportunitiesDescription?: string;
  /** Cloudflare Turnstile token (omitted when the widget is disabled). */
  turnstileToken?: string;
}

export interface StateOption {
  code: string;
  name: string;
}

/** Error carrying the HTTP status so callers can branch (e.g. 409 = duplicate). */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Submit a supplier onboarding application. Resolves on success; throws an
 * ApiError (with `status`) on failure. `status = 0` means the request never
 * reached the server (misconfig / network).
 */
export async function submitStartFreeApplication(payload: StartFreeApplication): Promise<void> {
  if (!API_URL) {
    throw new ApiError('The application service is not configured. Please try again later.', 0);
  }

  let res: Response;
  try {
    res = await fetch(`${API_URL}/suppliers/onboarding`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new ApiError('Network error. Please check your connection and try again.', 0);
  }

  if (!res.ok) {
    let message = 'Something went wrong. Please try again.';
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
    } catch {
      // non-JSON error body — keep the default message
    }
    throw new ApiError(message, res.status);
  }
}

export interface ContactFormPayload {
  /** Which product form this lead came from. */
  formType: 'airport' | 'operator';
  name: string;
  company: string;
  email: string;
  /** Airport form only. */
  markets?: string;
  /** Operator form only. */
  menuUrl?: string;
  /** Cloudflare Turnstile token (omitted when the widget is disabled). */
  turnstileToken?: string;
}

/**
 * Submit a corporate-site product form lead (Airport / Operator). Resolves on
 * success; throws an ApiError (with `status`) on failure. The API validates the
 * Turnstile token and emails the lead to the internal recipients.
 */
export async function submitContactForm(payload: ContactFormPayload): Promise<void> {
  if (!API_URL) {
    throw new ApiError('The form service is not configured. Please try again later.', 0);
  }

  let res: Response;
  try {
    res = await fetch(`${API_URL}/suppliers/onboarding/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new ApiError('Network error. Please check your connection and try again.', 0);
  }

  if (!res.ok) {
    let message = 'Something went wrong. Please try again.';
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
    } catch {
      // non-JSON error body — keep the default message
    }
    throw new ApiError(message, res.status);
  }
}

/** Fetch the US states available for the onboarding form's picker. */
export async function fetchStates(): Promise<StateOption[]> {
  if (!API_URL) return [];
  const res = await fetch(`${API_URL}/suppliers/onboarding/states`);
  if (!res.ok) throw new ApiError('Could not load the list of states.', res.status);
  return res.json();
}
